import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import AnimatedModal from './AnimatedModal';
import { useCandidateT } from '../../i18n/candidate';

/**
 * Themed modal shown when the user tries to leave the screening flow (e.g. back button).
 * Explains that refreshing or closing may auto-submit and asks them to stay.
 * Matches TalentCrux theme (electric-blue / indigo).
 * Text props are English; they are translated here for non-English candidate journeys.
 */
export default function LeaveScreeningModal({
  isOpen,
  onClose,
  onStay,
  onLeave,
  title = 'Leave this page?',
  message = 'Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out.',
  stayLabel = 'Stay on page',
  leaveLabel = 'Leave anyway',
}) {
  const t = useCandidateT();
  const handleStay = () => {
    onStay?.();
    onClose?.();
  };

  const handleLeave = () => {
    onLeave?.();
    onClose?.();
  };

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={handleStay}
      contentClassName="max-w-md w-full rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden"
      backdropClassName="bg-black/40"
      ariaLabelledBy="leave-screening-modal-title"
    >
      <div className="p-0">
        <div className="bg-gradient-to-r from-electric-blue to-indigo-600 px-6 py-5 sm:px-8 sm:py-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
              <ExclamationTriangleIcon className="h-5 w-5 text-white" aria-hidden />
            </div>
            <div>
              <h2 id="leave-screening-modal-title" className="text-lg font-bold text-white sm:text-xl">
                {t(title)}
              </h2>
              <p className="text-sm text-blue-100 mt-0.5">{t('Your progress may be affected')}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 sm:px-8 sm:py-6">
          <p className="text-sm text-gray-700 leading-relaxed">{t(message)}</p>
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={handleLeave}
              className="w-full sm:w-auto rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
            >
              {t(leaveLabel)}
            </button>
            <button
              type="button"
              onClick={handleStay}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-electric-blue to-indigo-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-electric-blue/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              {t(stayLabel)}
            </button>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
}
